import React from "react";

import "./joint_venture_change_modal.less";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { REQUEST_STATUS } from "@src/constants";
import { mapRoleRequestData } from "@src/features/resource_plan/utils/resource_plan_table_utils";
import { useAgGrid } from "@src/hooks/use_ag_grid";
import { useUpdateRoleRequestMutation } from "@src/services/apis/projectsApi";
import { RootState, useAppSelector } from "@src/setupStore";
import {
  RoleRequest,
  UpdateRoleRequestPayload,
  ResourcePlanTableRowData,
} from "@src/types/role_request_types";
import { App, Modal } from "antd";

interface RequestInformationCardProps {
  showModal: boolean;
  setShowModal: (visible: boolean) => void;
  request: RoleRequest;
}

const JointVentureChangeModal: React.FC<RequestInformationCardProps> = ({
  showModal,
  setShowModal,
  request,
}) => {
  const { message } = App.useApp();
  const { gridApi } = useAgGrid();
  const [updateRoleRequestMutation] = useUpdateRoleRequestMutation();

  // States
  const { projectId, jointVenturePartner } = useAppSelector(
    (state: RootState) => state.resourcePlanSlice
  );

  /**
   * Generates the modal title based on current joint venture status.
   * @returns {string}
   */
  function generateTitle(): string {
    return `${
      !request?.jointVenture ? "Change to" : "Remove"
    } 'Joint Venture' request`;
  }

  /**
   * Generates the modal body description explaining consequences of the action.
   * @returns {JSX.Element}
   */
  function generateDescription(): React.ReactElement {
    if (request.jointVenture) {
      return getRemoveJointVentureLabel();
    } else {
      return getAddJointVentureLabel();
    }
  }

  /**
   * Generates the label shown when toggling the "Joint Venture" flag,
   * depending on the current request status.
   *
   * If the request is in DRAFT status, a draft-specific label is returned.
   * For all other statuses, a different label is provided.
   *
   *  @returns A React element containing the appropriate message.
   */
  function getAddJointVentureLabel(): React.ReactElement {
    const addNonDraftJointVenture = (
      <p data-testid="jv-label">
        If you change this to a "Joint Venture" request,{" "}
        <span className="jvc-text-bold">
          the request will be removed from the team lead
        </span>{" "}
        and the
        <span className="jvc-text-bold">
          {" "}
          state will be changed to "confirmed".
        </span>{" "}
        Do you want to proceed?
      </p>
    );

    const addDraftJointVenture = (
      <p data-testid="jv-label">
        If you change this to a "Joint Venture" request and send it,{" "}
        <span className="jvc-text-bold">
          the status will be changed to "confirmed".
        </span>{" "}
        Do you want to proceed?
      </p>
    );

    return request.status === REQUEST_STATUS.DRAFT
      ? addDraftJointVenture
      : addNonDraftJointVenture;
  }

  /**
   * Generates the label shown when disabling the "Joint Venture" flag,
   * based on the current request status.
   *
   * If the request is in DRAFT status, a draft-specific label is returned.
   * For non-draft statuses, a different label is shown to reflect the
   * consequences of removing the Joint Venture flag.
   *
   * @returns A React element containing the appropriate message.
   */
  function getRemoveJointVentureLabel() {
    const removeNonDraftJointVenture = (
      <p data-testid="jv-label">
        If you remove "Joint Venture",{" "}
        <span className="jvc-text-bold">
          the request will be sent again to the team lead
        </span>
        . Do you want to proceed?
      </p>
    );

    const removeDraftJointVenture = (
      <p data-testid="jv-label">
        If you remove "Joint Venture",
        <span className="jvc-text-bold">
          {" "}
          the request will change to a normal draft request.{" "}
        </span>
        Do you want to proceed?
      </p>
    );

    return request.status === REQUEST_STATUS.DRAFT
      ? removeDraftJointVenture
      : removeNonDraftJointVenture;
  }

  /**
   * Handles the confirmation action: updates the request on backend and grid.
   */
  async function onConfirmSend() {
    if (!request) return;

    const newJointVentureValue = !request.jointVenture;
    const updateRequestPayload: UpdateRoleRequestPayload = {
      isJointVenture: newJointVentureValue,
    };

    try {
      const roleRequest: RoleRequest = await updateRoleRequestMutation({
        projectId: projectId,
        roleRequestId: request.roleRequestId.toString(),
        requestPayload: updateRequestPayload,
      }).unwrap();

      if (!roleRequest) return;

      const mappedRoleRequest: ResourcePlanTableRowData[] = mapRoleRequestData(
        [roleRequest],
        jointVenturePartner
      );
      gridApi.applyTransaction({ update: mappedRoleRequest });

      const rowNode = gridApi.getRowNode(roleRequest.roleRequestId.toString());
      gridApi.refreshCells({
        rowNodes: [rowNode],
        columns: ["status", "standardRoleName"],
        force: true,
      });

      message.success(
        `SAVED - Joint Venture - changed to ${newJointVentureValue}`
      );
    } catch (error) {
      console.error("Error updating joint venture", error);
      message.error(`Error updating joint venture to ${newJointVentureValue}`);
    } finally {
      setShowModal(false);
    }
  }

  /**
   * Handles cancel action: closes the modal.
   */
  function onCancel() {
    setShowModal(false);
  }

  return (
    <Modal
      open={showModal}
      centered
      width={668}
      maskClosable={false}
      closable={false}
      title={generateTitle()}
      okButtonProps={{
        icon: <CheckOutlined />,
        size: "large",
        "data-testid": "confirm-button",
      }}
      onOk={() => onConfirmSend()}
      okText="Confirm"
      cancelButtonProps={{
        icon: <CloseOutlined />,
        size: "large",
        "data-testid": "close-next-step-modal",
      }}
      onCancel={() => onCancel()}
      className="joint-venture-change-modal"
    >
      <div className="joint-venture-change-modal__body">
        {generateDescription()}
      </div>
    </Modal>
  );
};

export default JointVentureChangeModal;
